import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Table, Pagination, Modal, Button, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getTopicStats, getTopics, getTopicDocuments } from '@/services/api';

interface Topic {
  code: string;
  name: string;
  level: number;
  keywords: string[];
  doc_count: number;
}

interface TopicStats {
  coverage: { covered: number; total: number; coverage_pct: number };
  by_method: { match_type: string; cnt: number }[];
  top_topics: { code: string; name: string; doc_count: number }[];
}

interface TopicDoc {
  docid: string;
  docname: string;
  docstate: string;
  adoption_number: string;
  adoption_date: string;
  confidence: number;
  match_type: string;
}

const methodColors: Record<string, string> = {
  keyword_max: 'primary', cross: 'info', keyword_docname: 'secondary',
  keyword_max_v2: 'secondary', keyword_text_v2: 'secondary',
  keyword_weak: 'warning', keyword_auto: 'success',
  ml_tfidf: 'dark', target_law_v2: 'danger',
  bert_rubert_tiny2: 'purple', bert_rubert_tiny2_v2: 'purple',
  bert_auto: 'purple', fallback_auto: 'light', fallback_v2: 'light',
  keyword_final: 'primary', target_law: 'danger', target_law_v3: 'danger',
  ml_tfidf_v6: 'dark', tfidf_heuristic: 'dark', ml_tfidf_v2: 'dark', regex: 'danger',
};

const methodLabels: Record<string, string> = {
  keyword_max: 'Keyword MAX', cross: 'Cross', keyword_docname: 'Keyword',
  keyword_max_v2: 'Keyword v2', keyword_text_v2: 'Keyword Text',
  keyword_weak: 'Keyword Weak', keyword_auto: 'Keyword Auto',
  ml_tfidf: 'ML TF-IDF', target_law_v2: 'Target Law',
  bert_rubert_tiny2: 'BERT v1', bert_rubert_tiny2_v2: 'BERT v2',
  bert_auto: 'BERT Auto', fallback_auto: 'Fallback', fallback_v2: 'Fallback v2',
  keyword_final: 'Keyword Final', target_law: 'Target Law', target_law_v3: 'Target Law v3',
  ml_tfidf_v6: 'ML v6', tfidf_heuristic: 'Heuristic', ml_tfidf_v2: 'ML v2', regex: 'Regex',
};

export const TopicsPage: React.FC = () => {
  const [stats, setStats] = useState<TopicStats | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [topicDocs, setTopicDocs] = useState<TopicDoc[]>([]);
  const [topicDocsTotal, setTopicDocsTotal] = useState(0);
  const [topicDocsPage, setTopicDocsPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, topicsRes] = await Promise.all([
        getTopicStats(),
        getTopics(),
      ]);
      setStats(statsRes);
      setTopics(topicsRes.topics || []);
    } catch (err) {
      console.error('Failed to load topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const openTopic = async (topic: Topic) => {
    setSelectedTopic(topic);
    setShowModal(true);
    setTopicDocsPage(1);
    await loadTopicDocs(topic.code, 1);
  };

  const loadTopicDocs = async (code: string, page: number) => {
    setModalLoading(true);
    try {
      const res = await getTopicDocuments(code, page, 20);
      setTopicDocs(res.items || []);
      setTopicDocsTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to load topic docs:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setTopicDocsPage(page);
    if (selectedTopic) {
      loadTopicDocs(selectedTopic.code, page);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Загрузка тематической рубрикации...</p>
      </Container>
    );
  }

  const totalPages = Math.ceil(topicDocsTotal / 20);

  return (
    <Container className="py-4">
      <h4 className="mb-4 fw-bold">
        <i className="bi bi-tags me-2"></i>
        Тематическая рубрикация
      </h4>

      {stats && (
        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 text-muted">Покрытие документов</h6>
                  <i className="bi bi-pie-chart-fill text-primary fs-4"></i>
                </div>
                <div className="text-center">
                  <h2 className="mb-1 fw-bold text-primary">
                    {stats.coverage.coverage_pct}%
                  </h2>
                  <p className="text-muted mb-2">
                    {stats.coverage.covered.toLocaleString()} из {stats.coverage.total.toLocaleString()}
                  </p>
                  <ProgressBar
                    now={Number(stats.coverage.coverage_pct) as any}
                    variant={Number(stats.coverage.coverage_pct) > 90 ? 'success' : Number(stats.coverage.coverage_pct) > 70 ? 'warning' : 'danger'}
                    style={{ height: 8 }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={8}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <h6 className="mb-3 text-muted">Методы рубрикации</h6>
                <div className="d-flex flex-wrap gap-2">
                  {stats.by_method.map((m) => (
                    <Badge
                      key={m.match_type}
                      bg={methodColors[m.match_type] || 'secondary'}
                      className="px-3 py-2"
                    >
                      {methodLabels[m.match_type] || m.match_type}: {m.cnt.toLocaleString()}
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom-0 py-3">
          <h5 className="mb-0 fw-bold">Темы ({topics.length})</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Тема</th>
                  <th>Уровень</th>
                  <th>Ключевые слова</th>
                  <th className="text-end pe-4">Документов</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((topic) => (
                  <tr
                    key={topic.code}
                    onClick={() => openTopic(topic)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                          style={{
                            width: 36,
                            height: 36,
                            background: topic.level === 1 ? '#dbeafe' : '#f3e8ff',
                            color: topic.level === 1 ? '#2563eb' : '#9333ea',
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          {topic.name.charAt(0)}
                        </div>
                        <div>
                          <div className="fw-semibold">{topic.name}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {topic.code}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg={topic.level === 1 ? 'primary' : 'info'}>
                        {topic.level === 1 ? 'Основная' : 'Подтема'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(topic.keywords || []).slice(0, 3).map((kw, i) => (
                          <Badge key={i} bg="light" text="dark" className="fw-normal">
                            {kw}
                          </Badge>
                        ))}
                        {(topic.keywords || []).length > 3 && (
                          <Badge bg="light" text="muted" className="fw-normal">
                            +{(topic.keywords || []).length - 3}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <span className="fw-bold text-primary">
                        {topic.doc_count.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-folder me-2"></i>
            {selectedTopic?.name}
            <Badge bg="primary" className="ms-2">
              {topicDocsTotal.toLocaleString()} док.
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <div className="list-group">
                {topicDocs.map((doc) => (
                  <div
                    key={doc.docid}
                    className="list-group-item list-group-item-action"
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <Link
                          to={`/actual/document/${doc.docid}`}
                          className="text-decoration-none fw-semibold"
                          onClick={() => setShowModal(false)}
                        >
                          {doc.docname}
                        </Link>
                        <div className="d-flex gap-2 mt-1">
                          <Badge bg="light" text="dark" className="fw-normal">
                            {doc.adoption_number || '—'}
                          </Badge>
                          <Badge bg="light" text="dark" className="fw-normal">
                            {doc.adoption_date
                              ? new Date(doc.adoption_date).toLocaleDateString('ru-RU')
                              : '—'}
                          </Badge>
                          <Badge
                            bg={methodColors[doc.match_type] || 'secondary'}
                          >
                            {methodLabels[doc.match_type] || doc.match_type}
                          </Badge>
                          <Badge bg="light" text="dark" className="fw-normal">
                            conf: {(doc.confidence * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={topicDocsPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(topicDocsPage - 1)}
                      disabled={topicDocsPage === 1}
                    />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (topicDocsPage <= 3) {
                        pageNum = i + 1;
                      } else if (topicDocsPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = topicDocsPage - 2 + i;
                      }
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === topicDocsPage}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => handlePageChange(topicDocsPage + 1)}
                      disabled={topicDocsPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={topicDocsPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
